import { useMemo, type ReactNode } from "react";
import { MarkdownReact } from "@/components/MarkdownReact";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { formatWeight } from "@/lib/utils";
import {
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
  getQuoteDisplayTotals,
} from "@/lib/quoteCurrency";
import {
  formatAddressLines,
  getDiscountLines,
  computeLineNumbers,
  getBreakWeightUnit,
  getTotalWeightsByUnit,
  getItemLineDescription,
} from "../utils";
import {
  WEIGHT_UNIT_ABBREVIATION,
  type PublicQuote,
  type PublicQuoteAsset,
  type PublicQuoteDiscountLine,
  type PublicQuoteItemLine,
  type PublicQuoteLine,
} from "@forgepoint/shared";
import { formatDateOnly } from "@forgepoint/shared";
import { PdfPageViewer, type QuoteSection } from "../components/PdfPageViewer";

const IMAGE_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".tif",
  ".tiff",
];

const getAssetPreview = (
  asset: PublicQuoteAsset | null | undefined,
): { src: string; alt: string } | null => {
  if (!asset) {
    return null;
  }
  const fileName = asset.webViewFileName ?? asset.fileName;
  const fileType = asset.webViewFileType ?? asset.fileType;
  const isImage =
    (fileType ? fileType.startsWith("image/") : false) ||
    IMAGE_EXTENSIONS.some((ext) => fileName.toLowerCase().endsWith(ext));
  if (!isImage) {
    return null;
  }
  const src = asset.webViewExternalUrl ?? asset.externalUrl;
  if (!src) {
    return null;
  }
  return { src, alt: asset.fileName };
};

interface PublicQuoteTemplateModernProps {
  quote: PublicQuote;
  isExpired: boolean;
}

/**
 * Modern public quote template.
 * Renders content as paginated US Letter-sized pages via PdfPageViewer so the
 * quote can be exported to PDF with consistent page breaks.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 */
export function PublicQuoteTemplateModern({
  quote,
  isExpired,
}: PublicQuoteTemplateModernProps) {
  const { sections } = useModernSections(quote, isExpired);
  return <PdfPageViewer sections={sections} watermark={quote.watermark} />;
}

interface ModernLayout {
  sections: QuoteSection[];
}

/**
 * Builds the ordered, pageable sections for the Modern layout.
 * The company header appears once at the top of the first content page.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 * @returns Sections for PdfPageViewer
 */
function useModernSections(
  quote: PublicQuote,
  isExpired: boolean,
): ModernLayout {
  const lines = useMemo(() => quote.lines ?? [], [quote.lines]);
  const lineSequenceNumbers = useMemo(() => computeLineNumbers(lines), [lines]);
  const discountLines: PublicQuoteDiscountLine[] = useMemo(
    () => getDiscountLines(lines),
    [lines],
  );
  const totalWeightsByUnit = useMemo(
    () => getTotalWeightsByUnit(lines),
    [lines],
  );

  const sections: QuoteSection[] = [];

  const hasCoverLetter =
    !!quote.enableCoverLetter && !!quote.coverLetterMarkdown;

  if (hasCoverLetter) {
    sections.push({
      key: "cover-letter",
      content: (
        <Card className="p-6 bg-white border border-gray-200 shadow-sm break-inside-avoid">
          <div className="prose prose-sm max-w-none text-gray-700">
            <MarkdownReact>{quote.coverLetterMarkdown}</MarkdownReact>
          </div>
        </Card>
      ),
    });
  }

  // --- Intro: company header, customer/ship-to, quote metadata, expiry alert ---
  sections.push({
    key: "intro",
    forcePageBreak: hasCoverLetter,
    content: <ModernIntro quote={quote} isExpired={isExpired} />,
  });

  // --- One section per quote line (items, section headers, notes) ---
  lines.forEach((line, index) => {
    const content = renderModernLine(
      line,
      index,
      lineSequenceNumbers[index],
      quote.showTotalWeight ?? false,
      quote,
    );
    if (!content) {
      return;
    }
    sections.push({ key: `line-${index}`, content });
  });

  // --- Totals (subtotal, discounts, total, total weight) ---
  if (quote.showTotals) {
    sections.push({
      key: "totals",
      content: (
        <ModernTotals
          quote={quote}
          discountLines={discountLines}
          totalWeightsByUnit={totalWeightsByUnit}
        />
      ),
    });
  }

  // --- Quote footer ---
  if (quote.quoteFooter) {
    sections.push({
      key: "footer",
      content: (
        <div className="border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-500 font-bold whitespace-pre-wrap">
            {quote.quoteFooter}
          </p>
        </div>
      ),
    });
  }

  // --- Commercial Terms (delivery, payment) on a new page ---
  const hasCommercialTerms = !!quote.deliveryTerms || !!quote.paymentTerms;
  if (hasCommercialTerms) {
    sections.push({
      key: "commercial-terms",
      forcePageBreak: true,
      content: <ModernCommercialTerms quote={quote} />,
    });
  }

  // --- Terms and Conditions on its own page ---
  if (quote.termsOfService) {
    sections.push({
      key: "terms-of-service",
      forcePageBreak: true,
      content: (
        <ModernTermsAndConditions termsOfService={quote.termsOfService} />
      ),
    });
  }

  // --- Signature (already-signed quotes) ---
  if (quote.isSigned && quote.signature) {
    sections.push({
      key: "signature",
      content: (
        <Card className="p-6 bg-white border border-gray-200 shadow-sm break-inside-avoid">
          <div className="text-center">
            <div className="text-sm font-medium text-gray-500 mb-2">
              Quote Accepted &amp; Signed
            </div>
            <div className="font-signature text-xl text-gray-900">
              {quote.signature.name}
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {new Date(quote.signature.signedAt).toLocaleDateString()}
            </div>
          </div>
        </Card>
      ),
    });
  }

  return { sections };
}

/** Renders the line number badge, or null when there is no number. */
function renderLineNumberBadge(lineNumber: number | null) {
  if (!lineNumber) {
    return null;
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-800">
      {lineNumber}
    </div>
  );
}

interface ModernIntroProps {
  quote: PublicQuote;
  isExpired: boolean;
}

/**
 * Renders the company header, customer/ship-to, quote metadata and expiry alert.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 */
function ModernIntro({ quote, isExpired }: ModernIntroProps) {
  const organizationAddressLines = formatAddressLines(
    quote.organization?.address,
  );

  return (
    <div>
      {quote.organization && (
        <div className="mb-6 border-b border-gray-200 pb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              {quote.organization.logoUrl ? (
                <img
                  src={quote.organization.logoUrl}
                  alt={`${quote.organization.name} logo`}
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <div className="text-base font-bold text-gray-900">
                  {quote.organization.name}
                </div>
              )}
            </div>
            {quote.location ? (
              <div className="text-left sm:text-right space-y-1">
                {quote.location.logoUrl && (
                  <img
                    src={quote.location.logoUrl}
                    alt={`${quote.location.name} logo`}
                    className="h-10 w-auto object-contain sm:ml-auto"
                  />
                )}
                <div className="text-sm font-semibold text-gray-900">
                  {quote.location.name}
                </div>
                {formatAddressLines(quote.location.address) && (
                  <div className="text-xs text-gray-600 space-y-0.5">
                    {formatAddressLines(quote.location.address)?.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                )}
                {(quote.organization?.phone || quote.organization?.fax) && (
                  <p className="text-xs text-gray-600">
                    {quote.organization?.phone && (
                      <span>P: {quote.organization.phone}</span>
                    )}
                    {quote.organization?.fax && (
                      <span> / F: {quote.organization.fax}</span>
                    )}
                  </p>
                )}
                {quote.organization?.website && (
                  <a
                    href={
                      quote.organization.website.startsWith("http")
                        ? quote.organization.website
                        : `https://${quote.organization.website}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {quote.organization.website}
                  </a>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-600 text-left sm:text-right space-y-0.5">
                {organizationAddressLines?.map((line) => (
                  <p key={line}>{line}</p>
                ))}
                <p>
                  {quote.organization.phone && (
                    <span>P: {quote.organization.phone}</span>
                  )}
                  {quote.organization.fax && (
                    <span> / F: {quote.organization.fax}</span>
                  )}
                </p>
                {quote.organization.website && (
                  <a
                    href={
                      quote.organization.website.startsWith("http")
                        ? quote.organization.website
                        : `https://${quote.organization.website}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {quote.organization.website}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer & Ship To */}
      {(quote.customerCompany || quote.shippingAddress) && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {quote.customerCompany && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Customer
              </p>
              <p className="text-sm font-medium text-gray-900">
                {quote.customerCompany.name}
              </p>
              {formatAddressLines(quote.customerCompany.address) && (
                <div className="text-xs text-gray-600 mt-1">
                  {formatAddressLines(quote.customerCompany.address)?.map(
                    (line) => (
                      <p key={line}>{line}</p>
                    ),
                  )}
                </div>
              )}
            </div>
          )}
          {quote.shippingAddress && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                Ship To
              </p>
              {quote.shippingAddress.name && (
                <p className="text-sm font-medium text-gray-900">
                  {quote.shippingAddress.name}
                </p>
              )}
              <div className="text-xs text-gray-600 mt-1">
                {formatAddressLines(quote.shippingAddress)?.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quote Metadata */}
      <div className="mb-6">
        {quote.name && (
          <h2 className="text-sm font-semibold text-gray-900 mb-2">
            {quote.name}
          </h2>
        )}
        <div className="text-xs text-gray-600 space-y-1">
          <p>
            <span className="text-gray-500">Quote Number:</span>
            <span className="ml-2 font-medium">
              #{quote.quoteDisplayNumber}
            </span>
          </p>
          <p>
            <span className="text-gray-500">Revision:</span>
            <span className="ml-2 font-medium">{quote.revision}</span>
          </p>
          {quote.expirationDate && (
            <p>
              <span className="text-gray-500">Valid Until:</span>
              <span className="ml-2 font-medium">
                {formatDateOnly(quote.expirationDate)}
              </span>
            </p>
          )}
          {quote.salesRepName && (
            <p>
              <span className="text-gray-500">Sales Rep:</span>
              <span className="ml-2 font-medium">{quote.salesRepName}</span>
            </p>
          )}
        </div>
      </div>

      {isExpired && (
        <Alert className="mb-2 border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            This quote expired on{" "}
            {formatDateOnly(quote.expirationDate, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            . Please contact your sales representative for an updated quote.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

/**
 * Renders a single quote line (section header, note or item) for the Modern layout.
 * Returns null for discount lines, which are rendered within the totals section.
 * @param line - The hydrated quote line
 * @param index - The line's index, used as a stable React key
 * @param lineNumber - The computed display number for this line
 * @param showTotalWeight - Whether per-line weights should be displayed
 * @param quote - The hydrated quote data, used for currency formatting
 * @returns The rendered line content, or null
 */
function renderModernLine(
  line: PublicQuoteLine,
  index: number,
  lineNumber: number | null,
  showTotalWeight: boolean,
  quote: PublicQuote,
): ReactNode {
  if (line.type === "SECTION") {
    return (
      <Card
        key={index}
        className="p-4 bg-gray-50 border-gray-200 break-inside-avoid"
      >
        <div className="flex items-center gap-3">
          {renderLineNumberBadge(lineNumber)}
          <h3 className="whitespace-pre-line text-sm font-semibold text-gray-900">
            {line.description}
          </h3>
        </div>
      </Card>
    );
  }

  if (line.type === "NOTE") {
    return (
      <Card
        key={index}
        className="p-4 bg-amber-50 border-amber-200 break-inside-avoid"
      >
        <p className="whitespace-pre-line text-xs text-amber-900 italic">
          {line.description}
        </p>
      </Card>
    );
  }

  if (line.type === "ITEM") {
    return (
      <ModernItemLine
        line={line}
        index={index}
        lineNumber={lineNumber}
        showTotalWeight={showTotalWeight}
        quote={quote}
      />
    );
  }

  // Discount lines are rendered within the totals section.
  return null;
}

interface ModernItemLineProps {
  line: PublicQuoteItemLine;
  index: number;
  lineNumber: number | null;
  showTotalWeight: boolean;
  quote: PublicQuote;
}

/**
 * Renders an item line. Lines with quantity breaks show a pricing breakdown
 * table unless the only quantity is 1, where qty/unit labels are redundant.
 * @param line - The hydrated item line
 * @param index - The line's index, used as a stable React key
 * @param lineNumber - The computed display number for this line
 * @param showTotalWeight - Whether per-line weights should be displayed
 * @param quote - The hydrated quote data, used for currency formatting
 */
function ModernItemLine({
  line,
  index,
  lineNumber,
  showTotalWeight,
  quote,
}: ModernItemLineProps) {
  const quantityBreaks = line.quantityBreaks ?? [];
  const selectedQuantity = quantityBreaks[0]?.quantity;
  const itemImage = getAssetPreview(line.item?.primaryFileAsset ?? null);
  const itemDescription = getItemLineDescription(line);
  const hasSingleUnitQuantity =
    quantityBreaks.length === 1 && isQuantityOne(selectedQuantity);
  const showPricingBreakdown =
    quantityBreaks.length > 0 && !hasSingleUnitQuantity;

  return (
    <Card
      key={index}
      className="p-4 border-gray-200 hover:shadow-md transition-shadow break-inside-avoid"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-start justify-between gap-4">
          <div className="flex flex-1 gap-4">
            {renderLineNumberBadge(lineNumber)}

            {itemImage && (
              <div className="flex-shrink-0">
                <img
                  src={itemImage.src}
                  alt={itemImage.alt}
                  className="w-24 h-24 object-cover rounded-md"
                />
              </div>
            )}

            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900">
                {line.item?.name || line.description}
              </h4>
              {itemDescription && (
                <p className="whitespace-pre-line text-xs text-gray-600 mt-1">
                  {itemDescription}
                </p>
              )}
            </div>
          </div>

          {/* Price/Quantity Information */}
          <div className="w-full max-w-xs">
            {showPricingBreakdown ? (
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Pricing Breakdown
                </div>
                <div className="grid grid-cols-[1fr_auto_auto] gap-4 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500">
                  <span className="text-left">Qty</span>
                  <span className="text-right min-w-[100px]">Unit Price</span>
                  <span className="text-right min-w-[100px]">Line Total</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {quantityBreaks.map((qb, idx) => {
                    const isSelected = selectedQuantity === qb.quantity;
                    const weightUnit = getBreakWeightUnit(qb, line);
                    const baseClasses = isSelected
                      ? "bg-gray-100 font-semibold text-gray-900"
                      : "text-gray-700";
                    return (
                      <div
                        key={`${qb.quantity}-${idx}`}
                        className={`grid grid-cols-[1fr_auto_auto] gap-4 px-3 py-2 text-xs ${baseClasses}`}
                      >
                        <span className="text-left">
                          {qb.quantity}
                          {showTotalWeight && qb.totalWeight && weightUnit && (
                            <span className="block text-xs text-gray-500 font-normal mt-0.5">
                              Weight: {formatWeight(qb.totalWeight)}{" "}
                              {WEIGHT_UNIT_ABBREVIATION[weightUnit]}
                            </span>
                          )}
                        </span>
                        <span className="text-right min-w-[100px] tabular-nums">
                          {formatQuoteUnitPrice(qb, quote)}
                        </span>
                        <span className="text-right min-w-[100px] tabular-nums">
                          {formatQuoteLineTotal(qb, quote)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {line.amount && (
                  <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 text-xs">
                    <span className="text-gray-500">Selected Total</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatQuoteLineTotal(quantityBreaks[0], quote)}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-end gap-1">
                {!hasSingleUnitQuantity && (
                  <span className="text-xs text-gray-500">
                    {selectedQuantity ? `${selectedQuantity}` : "Qty —"}
                  </span>
                )}
                <span className="text-base font-semibold text-gray-900">
                  {formatQuoteLineTotal(quantityBreaks[0], quote)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function isQuantityOne(quantity: string | null | undefined) {
  if (!quantity) {
    return false;
  }

  const numericQuantity = Number(quantity.trim());
  return Number.isFinite(numericQuantity) && numericQuantity === 1;
}

interface ModernTotalsProps {
  quote: PublicQuote;
  discountLines: PublicQuoteDiscountLine[];
  totalWeightsByUnit: ReturnType<typeof getTotalWeightsByUnit>;
}

/**
 * Renders the totals section: subtotal, discounts, total and total weight.
 * @param quote - The hydrated quote data
 * @param discountLines - The quote's discount lines
 * @param totalWeightsByUnit - Aggregated total weights keyed by weight unit
 */
function ModernTotals({
  quote,
  discountLines,
  totalWeightsByUnit,
}: ModernTotalsProps) {
  const displayTotals = getQuoteDisplayTotals(quote);
  return (
    <div className="pt-4 border-t break-inside-avoid">
      <div className="space-y-2">
        {quote.subtotal && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-medium">
              {displayTotals.subtotalFormatted}
            </span>
          </div>
        )}

        {discountLines.length > 0 && (
          <div className="space-y-2 py-2">
            {discountLines.map((line, index) => {
              if (line.discount && line.amount !== null) {
                const discountAmount = formatQuoteLineTotal(line, quote);
                return (
                  <Card
                    key={index}
                    className="p-3 bg-green-50 border-green-200"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="whitespace-pre-line text-sm font-medium text-green-800">
                          {line.description}
                        </span>
                        {line.discount.type === "PERCENTAGE" && (
                          <span className="ml-2 text-xs text-green-600">
                            ({line.discount.percentage}% off)
                          </span>
                        )}
                      </div>
                      <span className="font-bold text-green-800">
                        -{discountAmount}
                      </span>
                    </div>
                  </Card>
                );
              }
              return null;
            })}
          </div>
        )}

        {quote.subtotalWithDiscounts &&
          quote.subtotal !== quote.subtotalWithDiscounts && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal (after discounts)</span>
              <span className="font-medium">
                {displayTotals.subtotalWithDiscountsFormatted}
              </span>
            </div>
          )}

        {quote.total && (
          <div className="flex justify-between text-base font-bold pt-2 border-t">
            <span>Total</span>
            <span>{displayTotals.totalFormatted}</span>
          </div>
        )}

        {quote.showTotalWeight &&
          totalWeightsByUnit.length > 0 &&
          totalWeightsByUnit.map(([unit, weight]) => (
            <div key={unit} className="flex justify-between text-sm pt-1">
              <span>Total Weight ({WEIGHT_UNIT_ABBREVIATION[unit]})</span>
              <span>{weight.toFixed(2)}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

interface ModernCommercialTermsProps {
  quote: PublicQuote;
}

/**
 * Renders the commercial terms (delivery and payment) grouped under a single
 * "Commercial Terms" heading, on its own page.
 * @param quote - The hydrated quote data
 */
function ModernCommercialTerms({ quote }: ModernCommercialTermsProps) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
        Commercial Terms
      </h2>
      <div className="space-y-6">
        {quote.deliveryTerms && (
          <ModernTermsCard title="Delivery Terms" body={quote.deliveryTerms} />
        )}
        {quote.paymentTerms && (
          <ModernTermsCard title="Payment Terms" body={quote.paymentTerms} />
        )}
      </div>
    </div>
  );
}

interface ModernTermsAndConditionsProps {
  termsOfService: string;
}

/**
 * Renders the full terms and conditions body under its own heading, on its own page.
 * @param termsOfService - The markdown terms of service content
 */
function ModernTermsAndConditions({
  termsOfService,
}: ModernTermsAndConditionsProps) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
        Terms and Conditions
      </h2>
      <div className="prose prose-xs text-gray-700 max-w-none text-xs">
        <MarkdownReact>{termsOfService}</MarkdownReact>
      </div>
    </div>
  );
}

interface ModernTermsCardProps {
  title: string;
  body: string;
}

/**
 * Renders a single commercial term block (delivery or payment terms).
 * @param title - The block heading
 * @param body - The markdown body content
 */
function ModernTermsCard({ title, body }: ModernTermsCardProps) {
  return (
    <div className="break-inside-avoid">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <div className="prose prose-xs text-gray-700 max-w-none text-xs">
        <MarkdownReact>{body}</MarkdownReact>
      </div>
    </div>
  );
}
